/*
 * Copyright (C) America Móvil S.A.B de C.V - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Juan Manuel Garcia Junco Moreno <manue.garcia@ironbit.com.mx>, May 2020
 */
import { fill } from '../../../core/lib';

/**
 * Makes an array of 128 uint8's which represent boolean values.
 * Spec is an array of ASCII code points or ASCII code point ranges
 * ranges are expressed as [start, end]
 * Create a table with the characters 0x30-0x39 (decimals '0' - '9') and
 * 0x7A (lowercaseletter 'z') as `true`:
 *
 * @ignore
 * @param spec ASCII character codes.
 * @returns ASCII code table.
 */
export function createASCIITable(spec: Array<number | number[]>): Uint8Array {
    const table = new Uint8Array(128);

    spec.forEach(item => {
        if (typeof item === 'number') {
            table[item] = 1;
        } else {
            const [start, end] = item;
            for (let j = start; j <= end; j += 1) {
                table[j] = 1;
            }
        }
    });

    return table;
}

/**
 * Creates an array of 128 strings that map to the URI encoded value of the string.
 * Spec is an array of characters, each index of the array maps the character code
 * of each value on the spec table to the escaped value of the char
 *
 * @ignore
 * @param spec ASCII characters.
 * @returns Escaped characters table.
 */
export function createEscapeTable(spec: string[]): string[] {
    const table = fill(new Array(128), '');

    for (let i = 0; i < spec.length; i += 1) {
        const char = spec[i];
        let encodedChar = encodeURIComponent(char);
        if (encodedChar === char) {
            encodedChar = escape(char);
        }
        table[char.charCodeAt(0)] = encodedChar;
    }

    return table;
}

/**
 * Search `char` (integer code for a character) in `string`
 * starting from `fromIndex` and ending at `string.length - 1` or when a stop character is found.
 *
 * @ignore
 * @param string String to search.
 * @param code Character code to search.
 * @param fromIndex Index to start searching.
 * @param stopCharacterTable A table of characters used to stop searching.
 * @returns True of the character is in the string before any stop character.
 */
export function searchCharacterCode(
    string: string,
    code: number,
    fromIndex: number,
    stopCharacterTable: Uint8Array
): boolean {
    const len = string.length;
    for (let i = fromIndex; i < len; i += 1) {
        const charCode = string.charCodeAt(i);

        if (charCode === code) {
            return true;
        }
        if (stopCharacterTable[charCode] === 1) {
            return false;
        }
    }
    return false;
}

/**
 * Check if a string contains at least one of a list of character codes.
 *
 * @ignore
 * @param string String to search.
 * @param codes Codes to search.
 * @returns True if the string contains any of the character codes.
 */
export function containsCharacterCodes(string: string, ...codes: number[]): boolean {
    const { length } = string;

    for (let i = 0; i < length; i += 1) {
        const code = string.charCodeAt(i);
        // eslint-disable-next-line @typescript-eslint/prefer-includes
        if (codes.indexOf(code) >= 0) return true;
    }
    return false;
}

/**
 * Removes one ending slash from a string (if any).
 *
 * @param string String to remove the slash from.
 * @returns The string but with the slash removed.
 */
export function removeEndingSlash(string: string): string {
    return string.endsWith('/') ? string.substring(0, string.length - 1) : string;
}



// WEBPACK FOOTER //
// ./src/packages/http/lib/URL/utils.ts